import { AlertIcon, CheckCircleIcon, ClockIcon, GoalIcon, XCircleIcon } from "@primer/octicons-react";
import { Link } from "react-router-dom";
import { rp } from "../api/client";
import type { OutcomeReport as Report, OutcomeState } from "../api/types";
import { Badge, type Tone } from "./ui/badge";
import { Card, CardHeader } from "./ui/card";
import { List, ListItem } from "./ui/list";

const STATE: Record<OutcomeState, { label: string; tone: Tone; Icon: typeof CheckCircleIcon }> = {
  proven: { label: "Pass", tone: "green", Icon: CheckCircleIcon },
  failed: { label: "Fail", tone: "red", Icon: XCircleIcon },
  unproven: { label: "Not yet", tone: "yellow", Icon: ClockIcon },
};

const ON_MAIN: Record<string, string> = { holds: "pass", broken: "fail", pending: "not yet" };

const RESULT: Record<string, { label: string; tone: Tone }> = {
  pass: { label: "pass", tone: "green" },
  fail: { label: "fail", tone: "red" },
  missing: { label: "missing", tone: "red" },
  not_reported: { label: "no report", tone: "yellow" },
  running: { label: "running", tone: "yellow" },
  not_run: { label: "not run", tone: "default" },
};

/**
 * The change as outcomes rather than code: for each requirement of the spec its task names,
 * whether its proofs passed on the commit that would land. A person reviewing outcomes decides
 * from this; the diff is one tab over.
 */
export function OutcomeReport({ repo, r, onChecks }: { repo: string; r: Report; onChecks: () => void }) {
  const total = r.outcomes.length;
  return (
    <div className="mt-4 flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-muted-foreground">
        <span className="text-base text-foreground">
          <b>
            {r.proven}/{total}
          </b>{" "}
          pass
        </span>
        {r.failed > 0 && <Badge tone="red">{r.failed} fail</Badge>}
        {r.unproven > 0 && <Badge tone="yellow">{r.unproven} not yet</Badge>}
        <span className="ml-auto flex flex-wrap items-center gap-2 text-xs">
          {r.intent && (
            <Badge asChild tone="blue">
              <Link to={`${rp(repo)}/intents/${r.intent}`}>
                <GoalIcon size={12} /> {r.intent}
              </Link>
            </Badge>
          )}
          {r.spec && (
            <Link to={`${rp(repo)}/docs?view=docs&select=${encodeURIComponent(r.spec)}`} className="font-mono">
              {r.spec}
            </Link>
          )}
        </span>
      </div>
      {r.self_authored > 0 && (
        <p className="m-0 flex items-start gap-2 rounded-md border border-warning-muted bg-warning-subtle px-3 py-2 text-sm">
          <AlertIcon size={16} className="mt-0.5 shrink-0 text-warning" />
          <span>
            {r.self_authored} test{r.self_authored === 1 ? "" : "s"} written with the code
          </span>
        </p>
      )}
      {r.outside && r.outside.length > 0 && (
        <p className="m-0 flex items-start gap-2 rounded-md border border-warning-muted bg-warning-subtle px-3 py-2 text-sm">
          <AlertIcon size={16} className="mt-0.5 shrink-0 text-warning" />
          <span>
            Out of scope: <span className="font-mono text-xs">{r.outside.join(", ")}</span>
          </span>
        </p>
      )}
      <Card className="mt-0">
        <CardHeader>Outcomes</CardHeader>
        <List>
          {r.outcomes.map((o) => {
            const s = STATE[o.state];
            return (
              <ListItem key={o.check} className="items-start py-3 hover:bg-transparent">
                <s.Icon size={16} className={`mt-1 shrink-0 ${s.tone === "green" ? "text-success" : s.tone === "red" ? "text-danger" : "text-warning"}`} />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <b className="text-foreground">{o.title || o.check}</b>
                    <Badge tone={s.tone}>{s.label}</Badge>
                    {/* main's state only when it differs from this change's */}
                    {(ON_MAIN[o.on_main] ?? o.on_main) !== s.label.toLowerCase() && <span className="text-xs text-muted-foreground">main: {ON_MAIN[o.on_main] ?? o.on_main}</span>}
                  </div>
                  <ul className="m-0 mt-2 flex list-none flex-col gap-1 p-0">
                    {o.proofs.map((p, i) => {
                      const res = RESULT[p.result] ?? RESULT.not_run;
                      return (
                        <li key={i} className="flex flex-wrap items-center gap-2 text-xs">
                          {p.proof.kind && <Badge tone="blue">{p.proof.kind}</Badge>}
                          <button type="button" className="font-mono hover:underline" onClick={onChecks} title={`${p.job ?? p.proof.job}: ${p.proof.file ? `${p.proof.file}::` : ""}${p.proof.test}`}>
                            {p.proof.test}
                          </button>
                          {/* the test's own result only when it is not what the row already says */}
                          {res.label !== s.label.toLowerCase() && <Badge tone={res.tone}>{res.label}</Badge>}
                          {p.self_authored && <Badge tone="orange">with the code</Badge>}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </ListItem>
            );
          })}
        </List>
      </Card>
    </div>
  );
}
